import { formatDate as formatDateUtil, formatDateTime as formatDateTimeUtil } from '../../utils/dateUtils';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { logout } from '../../store/slices/authSlice';
import { 
  Plus, 
  Search, 
  Filter, 
  Eye, 
  Edit, 
  Trash2, 
  Calendar,
  CreditCard,
  CheckCircle,
  XCircle,
  Settings,
  X,
  Save,
  AlertCircle
} from 'lucide-react';
import { API_BASE_URL } from '../../config/api';

interface PaymentMethod {
  payment_gateway_options_id: number;
  payment_method_name: string;
  display_name: string;
  description: string | null;
  username: string | null;
  email: string | null;
  phone: string | null;
  account_name: string | null;
  account_number: string | null;
  routing_number: string | null;
  qr_code: string | null;
  instructions: string | null;
  supported_currencies: string | null;
  minimum_amount: number | null;
  maximum_amount: number | null;
  status: string;
  is_default: boolean;
  sort_order: number;
  enable_for_users: boolean;
  allowed_countries: string | null;
  created_at: string;
  updated_at: string;
}

const AdminPaymentMethods: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingMethod, setEditingMethod] = useState<PaymentMethod | null>(null);
  const [deletingMethod, setDeletingMethod] = useState<PaymentMethod | null>(null);
  
  // Form states
  const [formData, setFormData] = useState({
    payment_method_name: '',
    display_name: '',
    username: '',
    email: '',
    phone: '',
    account_name: '',
    account_number: '',
    routing_number: '',
    description: '',
    instructions: '',
    supported_currencies: '',
    minimum_amount: '',
    maximum_amount: '',
    allowed_countries: '',
    status: 'active',
    is_default: false,
    enable_for_users: true,
    sort_order: 0
  });
  
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [formSuccess, setFormSuccess] = useState<string | null>(null);

  // Helper function to handle JWT expiration
  const handleJWTExpiration = () => {
    dispatch(logout());
    toast.error('Your session has expired. Please login again.');
    navigate('/');
  };

  // Fetch payment methods
  const fetchPaymentMethods = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('diasporawise_admin_token');
      
      if (!token) {
        handleJWTExpiration();
        return;
      }

      const response = await fetch(`${API_BASE_URL}/admin/payment-methods`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();
      console.log('Payment Methods API Response:', data);

      // Check for JWT expiration
      if (!data.success && (data.message?.toLowerCase().includes('jwt expired') || 
                            data.message?.toLowerCase().includes('token expired') ||
                            data.message?.toLowerCase().includes('unauthorized'))) {
        handleJWTExpiration();
        return;
      }

      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      if (data.success && data.data) {
        setPaymentMethods(data.data);
      } else {
        throw new Error(data.message || 'Failed to fetch payment methods');
      }

    } catch (err) {
      console.error('Error fetching payment methods:', err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      
      // Check if error message indicates JWT expiration
      if (errorMessage.toLowerCase().includes('jwt expired') || 
          errorMessage.toLowerCase().includes('token expired')) {
        handleJWTExpiration();
        return;
      }
      
      setError(`Failed to load payment methods: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  // Create payment method
  const createPaymentMethod = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError(null);
    setFormSuccess(null);

    try {
      const token = localStorage.getItem('diasporawise_admin_token');
      
      if (!token) {
        handleJWTExpiration();
        return;
      }

      // Build payload with only non-empty fields
      const payload: any = {
        payment_method_name: formData.payment_method_name,
        display_name: formData.display_name,
        status: formData.status,
        is_default: formData.is_default,
        enable_for_users: formData.enable_for_users,
        sort_order: parseInt(formData.sort_order.toString())
      };

      // Assign optional fields (with null fallback if empty/whitespace-only)
      payload.username = formData.username?.trim() || null;
      payload.email = formData.email?.trim() || null;
      payload.phone = formData.phone?.trim() || null;
      payload.account_name = formData.account_name?.trim() || null;
      payload.account_number = formData.account_number?.trim() || null;
      payload.routing_number = formData.routing_number?.trim() || null;
      payload.description = formData.description?.trim() || null;
      payload.instructions = formData.instructions?.trim() || null;
      payload.supported_currencies = formData.supported_currencies?.trim() || null;
      payload.allowed_countries = formData.allowed_countries?.trim() || null;
      payload.minimum_amount = formData.minimum_amount && formData.minimum_amount.trim() ? parseFloat(formData.minimum_amount) : null;
      payload.maximum_amount = formData.maximum_amount && formData.maximum_amount.trim() ? parseFloat(formData.maximum_amount) : null;


      console.log('Creating payment method with payload:', payload);

      const response = await fetch(`${API_BASE_URL}/admin/payment-methods`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      console.log('Create Payment Method Response:', data);

      // Check for JWT expiration
      if (!data.success && (data.message?.toLowerCase().includes('jwt expired') || 
                            data.message?.toLowerCase().includes('token expired') ||
                            data.message?.toLowerCase().includes('unauthorized'))) {
        handleJWTExpiration();
        return;
      }

      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      if (data.success) {
        setFormSuccess('Payment method created successfully!');
        setShowCreateModal(false);
        resetForm();
        fetchPaymentMethods();
      } else {
        throw new Error(data.message || 'Failed to create payment method');
      }

    } catch (err) {
      console.error('Error creating payment method:', err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      
      // Check if error message indicates JWT expiration
      if (errorMessage.toLowerCase().includes('jwt expired') || 
          errorMessage.toLowerCase().includes('token expired')) {
        handleJWTExpiration();
        return;
      }
      
      setFormError(`Failed to create payment method: ${errorMessage}`);
    } finally {
      setFormLoading(false);
    }
  };

  // Update payment method
  const updatePaymentMethod = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingMethod) return;

    setFormLoading(true);
    setFormError(null);
    setFormSuccess(null);

    try {
      const token = localStorage.getItem('diasporawise_admin_token');
      
      if (!token) {
        handleJWTExpiration();
        return;
      }

      // Build payload with only non-empty fields
      const payload: any = {
        payment_method_name: formData.payment_method_name,
        display_name: formData.display_name,
        status: formData.status,
        is_default: formData.is_default,
        enable_for_users: formData.enable_for_users,
        sort_order: parseInt(formData.sort_order.toString())
      };

      // Assign optional fields (with null fallback if empty/whitespace-only)
      payload.username = formData.username?.trim() || null;
      payload.email = formData.email?.trim() || null;
      payload.phone = formData.phone?.trim() || null;
      payload.account_name = formData.account_name?.trim() || null;
      payload.account_number = formData.account_number?.trim() || null;
      payload.routing_number = formData.routing_number?.trim() || null;
      payload.description = formData.description?.trim() || null;
      payload.instructions = formData.instructions?.trim() || null;
      payload.supported_currencies = formData.supported_currencies?.trim() || null;
      payload.allowed_countries = formData.allowed_countries?.trim() || null;
      payload.minimum_amount = formData.minimum_amount && formData.minimum_amount.trim() ? parseFloat(formData.minimum_amount) : null;
      payload.maximum_amount = formData.maximum_amount && formData.maximum_amount.trim() ? parseFloat(formData.maximum_amount) : null;


      console.log('Updating payment method with payload:', payload);

      const response = await fetch(`${API_BASE_URL}/admin/payment-methods/${editingMethod.payment_gateway_options_id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      console.log('Update Payment Method Response:', data);

      // Check for JWT expiration
      if (!data.success && (data.message?.toLowerCase().includes('jwt expired') || 
                            data.message?.toLowerCase().includes('token expired') ||
                            data.message?.toLowerCase().includes('unauthorized'))) {
        handleJWTExpiration();
        return;
      }

      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      if (data.success) {
        setFormSuccess('Payment method updated successfully!');
        setShowEditModal(false);
        setEditingMethod(null);
        resetForm();
        fetchPaymentMethods();
      } else {
        throw new Error(data.message || 'Failed to update payment method');
      }

    } catch (err) {
      console.error('Error updating payment method:', err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      
      // Check if error message indicates JWT expiration
      if (errorMessage.toLowerCase().includes('jwt expired') || 
          errorMessage.toLowerCase().includes('token expired')) {
        handleJWTExpiration();
        return;
      }
      
      setFormError(`Failed to update payment method: ${errorMessage}`);
    } finally {
      setFormLoading(false);
    }
  };

  // Delete payment method
  const deletePaymentMethod = async () => {
    if (!deletingMethod) return;

    setFormLoading(true);
    setFormError(null);

    try {
      const token = localStorage.getItem('diasporawise_admin_token');
      
      if (!token) {
        handleJWTExpiration();
        return;
      }

      const response = await fetch(`${API_BASE_URL}/admin/payment-methods/${deletingMethod.payment_gateway_options_id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();
      console.log('Delete Payment Method Response:', data);

      // Check for JWT expiration
      if (!data.success && (data.message?.toLowerCase().includes('jwt expired') || 
                            data.message?.toLowerCase().includes('token expired') ||
                            data.message?.toLowerCase().includes('unauthorized'))) {
        handleJWTExpiration();
        return;
      }

      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      if (data.success) {
        setFormSuccess('Payment method deleted successfully!');
        setShowDeleteModal(false);
        setDeletingMethod(null);
        fetchPaymentMethods();
      } else {
        throw new Error(data.message || 'Failed to delete payment method');
      }

    } catch (err) {
      console.error('Error deleting payment method:', err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      
      // Check if error message indicates JWT expiration
      if (errorMessage.toLowerCase().includes('jwt expired') || 
          errorMessage.toLowerCase().includes('token expired')) {
        handleJWTExpiration();
        return;
      }
      
      setFormError(`Failed to delete payment method: ${errorMessage}`);
    } finally {
      setFormLoading(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      payment_method_name: '',
      display_name: '',
      username: '',
      email: '',
      phone: '',
      account_name: '',
      account_number: '',
      routing_number: '',
      description: '',
      instructions: '',
      supported_currencies: '',
      minimum_amount: '',
      maximum_amount: '',
      allowed_countries: '',
      status: 'active',
      is_default: false,
      enable_for_users: true,
      sort_order: 0
    });
    setFormError(null);
    setFormSuccess(null);
  };

  // Handle edit
  const handleEdit = (method: PaymentMethod) => {
    setEditingMethod(method);
    setFormData({
      payment_method_name: method.payment_method_name,
      display_name: method.display_name,
      username: method.username || '',
      email: method.email || '',
      phone: method.phone || '',
      account_name: method.account_name || '',
      account_number: method.account_number || '',
      routing_number: method.routing_number || '',
      description: method.description || '',
      instructions: method.instructions || '',
      supported_currencies: method.supported_currencies || '',
      minimum_amount: method.minimum_amount?.toString() || '',
      maximum_amount: method.maximum_amount?.toString() || '',
      allowed_countries: method.allowed_countries || '',
      status: method.status,
      is_default: method.is_default,
      enable_for_users: method.enable_for_users,
      sort_order: method.sort_order
    });
    setShowEditModal(true);
  };

  // Handle delete
  const handleDelete = (method: PaymentMethod) => {
    setDeletingMethod(method);
    setShowDeleteModal(true);
  };

  // Filter payment methods
  const filteredMethods = paymentMethods.filter(method => {
    const matchesSearch = method.payment_method_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         method.display_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (method.username && method.username.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesStatus = statusFilter === 'all' || method.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'inactive': return 'bg-red-100 text-red-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  useEffect(() => {
    fetchPaymentMethods();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-800 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading payment methods...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="text-red-600 mb-4">
            <CreditCard className="w-12 h-12 mx-auto mb-2" />
            <p className="text-lg font-medium">Error Loading Payment Methods</p>
          </div>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={fetchPaymentMethods}
            className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-gray-800 rounded-lg flex items-center justify-center">
            <CreditCard className="w-4 h-4 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Payment Methods</h2>
            <p className="text-sm text-gray-600">Manage payment gateway options</p>
          </div>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Payment Method
        </button>
      </div>

      {/* Success/Error Messages */}
      {formSuccess && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <p className="text-green-800">{formSuccess}</p>
          </div>
        </div>
      )}

      {formError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-600" />
            <p className="text-red-800">{formError}</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search payment methods..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
            />
          </div>
        </div>
        <div className="sm:w-48">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="pending">Pending</option>
          </select>
        </div>
      </div>

      {/* Payment Methods Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Payment Method
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Username/Account
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Default
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredMethods.map((method) => (
                <tr key={method.payment_gateway_options_id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{method.display_name}</div>
                      <div className="text-sm text-gray-500">{method.payment_method_name}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="space-y-1">
                      {method.username && (
                        <div className="text-sm text-gray-900">
                          <span className="font-semibold text-gray-500">Username: </span>
                          {method.username}
                        </div>
                      )}
                      {method.phone && (
                        <div className="text-sm text-gray-900">
                          <span className="font-semibold text-gray-500">Phone: </span>
                          {method.phone}
                        </div>
                      )}
                      {method.account_name && (
                        <div className="text-sm text-gray-900">
                          <span className="font-semibold text-gray-500">Acc Name: </span>
                          {method.account_name}
                        </div>
                      )}
                      {method.account_number && (
                        <div className="text-sm text-gray-900">
                          <span className="font-semibold text-gray-500">Acc Num: </span>
                          {method.account_number}
                        </div>
                      )}
                      {method.email && (
                        <div className="text-sm text-gray-500">
                          <span className="font-semibold text-gray-400">Email: </span>
                          {method.email}
                        </div>
                      )}
                      {!method.username && !method.phone && !method.account_name && !method.account_number && !method.email && (
                        <div className="text-sm text-gray-400">N/A</div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(method.status)}`}>
                      {method.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {method.is_default ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : (
                      <XCircle className="w-5 h-5 text-gray-400" />
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDateUtil(method.created_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleEdit(method)}
                        className="text-green-600 hover:text-green-700 p-2 hover:bg-green-50 rounded-lg transition-all duration-200 border border-transparent hover:border-green-300 hover:shadow-sm"
                        title="Edit"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(method)}
                        className="text-red-600 hover:text-red-900"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredMethods.length === 0 && (
          <div className="text-center py-12">
            <CreditCard className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No payment methods found</h3>
            <p className="text-gray-600">No payment methods match your current filters.</p>
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Create Payment Method</h3>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={createPaymentMethod} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Payment Method Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.payment_method_name}
                    onChange={(e) => setFormData({...formData, payment_method_name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                    placeholder="e.g., Zelle1"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Display Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.display_name}
                    onChange={(e) => setFormData({...formData, display_name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                    placeholder="e.g., Bank Of Zelle"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Username
                  </label>
                  <input
                    type="text"
                    value={formData.username}
                    onChange={(e) => setFormData({...formData, username: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                    placeholder="e.g., americalzellebank"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="pending">Pending</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                  placeholder="Payment method description..."
                />
              </div>

              <div className="flex items-center gap-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.is_default}
                    onChange={(e) => setFormData({...formData, is_default: e.target.checked})}
                    className="rounded border-gray-300 text-gray-600 focus:ring-gray-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Set as default</span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.enable_for_users}
                    onChange={(e) => setFormData({...formData, enable_for_users: e.target.checked})}
                    className="rounded border-gray-300 text-gray-600 focus:ring-gray-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Enable for users</span>
                </label>
              </div>

              <div className="flex items-center gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    resetForm();
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={formLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={formLoading}
                  className="flex-1 px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {formLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Creating...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      Create Payment Method
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editingMethod && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Edit Payment Method</h3>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingMethod(null);
                  resetForm();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={updatePaymentMethod} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Payment Method Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.payment_method_name}
                    onChange={(e) => setFormData({...formData, payment_method_name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Display Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.display_name}
                    onChange={(e) => setFormData({...formData, display_name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Username
                  </label>
                  <input
                    type="text"
                    value={formData.username}
                    onChange={(e) => setFormData({...formData, username: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="pending">Pending</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                />
              </div>

              <div className="flex items-center gap-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.is_default}
                    onChange={(e) => setFormData({...formData, is_default: e.target.checked})}
                    className="rounded border-gray-300 text-gray-600 focus:ring-gray-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Set as default</span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.enable_for_users}
                    onChange={(e) => setFormData({...formData, enable_for_users: e.target.checked})}
                    className="rounded border-gray-300 text-gray-600 focus:ring-gray-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Enable for users</span>
                </label>
              </div>

              <div className="flex items-center gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingMethod(null);
                    resetForm();
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={formLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={formLoading}
                  className="flex-1 px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {formLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Updating...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      Update Payment Method
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && deletingMethod && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Delete Payment Method</h3>
                <p className="text-sm text-gray-600">This action cannot be undone.</p>
              </div>
            </div>

            <div className="mb-6">
              <p className="text-gray-700">
                Are you sure you want to delete <strong>{deletingMethod.display_name}</strong>?
              </p>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeletingMethod(null);
                }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                disabled={formLoading}
              >
                Cancel
              </button>
              <button
                onClick={deletePaymentMethod}
                disabled={formLoading}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {formLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    Delete
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPaymentMethods;
